(() => {
if (window.islanderPolicyNavigationReady) return;
window.islanderPolicyNavigationReady = true;

class IslanderPolicyNavigation {
  constructor(section) {
    this.section = section;
    this.override = section.querySelector('[data-policy-override]');
    this.native = document.querySelector('[data-policy-native]');
    if (this.native) this.native.hidden = Boolean(this.override);

    const contentRoot = this.override || this.native;
    this.body = contentRoot?.querySelector('[data-policy-body]');
    this.container = contentRoot?.querySelector('[data-policy-toc-container]');
    this.list = contentRoot?.querySelector('[data-policy-toc]');
    this.observer = null;

    if (!this.body || !this.container || !this.list) return;

    this.build();
    this.onHashChange = () => {
      const id = window.location.hash.slice(1);
      if (this.list.querySelectorAll('[data-policy-toc-link]').length) this.setCurrent(id);
    };
    window.addEventListener('hashchange', this.onHashChange);
  }

  build() {
    const policyPath = window.location.pathname;
    const selector = this.section.dataset.policyHeadings || 'h1, h2, h3, strong';
    const headings = Array.from(this.body.querySelectorAll(selector)).filter((heading) => {
      const text = heading.textContent.trim();
      if (!text || heading.closest('.shopify-policy__title')) return false;
      if (heading.tagName !== 'STRONG') return true;
      if (heading.closest('h1, h2, h3')) return false;
      if (policyPath.endsWith('/terms-of-service')) return /^(OVERVIEW|SECTION\s+\d+\s*[-–—])/i.test(text);
      if (policyPath.endsWith('/refund-policy')) return heading.parentElement.tagName === 'P' && heading.parentElement.textContent.trim().startsWith(text);
      return false;
    });

    if (headings.length < 2) return;

    const usedIds = new Set();
    const fragment = document.createDocumentFragment();

    headings.forEach((heading, index) => {
      const baseId = this.slugify(heading.textContent) || `policy-section-${index + 1}`;
      let headingId = heading.id || baseId;
      let duplicateIndex = 2;

      while (usedIds.has(headingId) || (document.getElementById(headingId) && document.getElementById(headingId) !== heading)) {
        headingId = `${baseId}-${duplicateIndex}`;
        duplicateIndex += 1;
      }

      heading.id = headingId;
      heading.dataset.policyAnchor = '';
      usedIds.add(headingId);

      const item = document.createElement('li');
      const link = document.createElement('a');
      link.className = 'islander-policy-page__toc-link';
      link.href = `#${headingId}`;
      link.textContent = heading.textContent.trim();
      link.dataset.policyTocLink = headingId;
      item.append(link);
      fragment.append(item);
    });

    this.list.replaceChildren(fragment);
    this.container.hidden = false;
    const pickerLabel = document.createElement('label');
    pickerLabel.className = 'islander-policy-page__picker';
    pickerLabel.textContent = this.section.dataset.navigationLabel || 'On this page';
    const picker = document.createElement('select');
    picker.setAttribute('aria-label', this.section.dataset.jumpLabel || 'Jump to a policy section');
    picker.add(new Option(this.section.dataset.jumpPlaceholder || 'Choose a section', ''));
    headings.forEach((heading) => picker.add(new Option(heading.textContent.trim(), heading.id)));
    picker.addEventListener('change', () => {
      if (!picker.value) return;
      const target = document.getElementById(picker.value);
      if (!target) return;
      window.location.hash = picker.value;
      // Re-selecting an existing fragment must still return to its heading.
      target.scrollIntoView({ block: 'start', behavior: 'instant' });
      this.setCurrent(picker.value);
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    });
    pickerLabel.append(picker);
    this.picker = picker;
    this.container.prepend(pickerLabel);
    this.section.dataset.policyReady = 'true';
    this.setCurrent(window.location.hash.slice(1) || headings[0].id);
    this.observe(headings);
  }

  setCurrent(id) {
    const links = Array.from(this.list.querySelectorAll('[data-policy-toc-link]'));
    if (!links.some((link) => link.dataset.policyTocLink === id)) return;
    links.forEach((link) => {
      if (link.dataset.policyTocLink === id) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    });
    if (this.picker) this.picker.value = id;
  }

  slugify(value) {
    return value
      .trim()
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  observe(headings) {
    if (!('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((first, second) => first.boundingClientRect.top - second.boundingClientRect.top)[0];

        if (!visible) return;

        this.setCurrent(visible.target.id);
      },
      { rootMargin: '-20% 0px -65% 0px', threshold: 0 },
    );

    headings.forEach((heading) => this.observer.observe(heading));
  }

  destroy() {
    this.observer?.disconnect();
    window.removeEventListener('hashchange', this.onHashChange);
    this.container?.querySelector('.islander-policy-page__picker')?.remove();
    delete this.section.dataset.policyReady;
  }
}

const islanderPolicySections = new Map();

const initialisePolicySection = (section) => {
  if (!section || islanderPolicySections.has(section)) return;
  islanderPolicySections.set(section, new IslanderPolicyNavigation(section));
};

document.querySelectorAll('[data-islander-policy-page]').forEach(initialisePolicySection);

document.addEventListener('shopify:section:load', (event) => {
  initialisePolicySection(event.target.querySelector('[data-islander-policy-page]'));
});

document.addEventListener('shopify:section:unload', (event) => {
  const section = event.target.querySelector('[data-islander-policy-page]');
  if (!section) return;

  islanderPolicySections.get(section)?.destroy();
  islanderPolicySections.delete(section);
});

document.addEventListener('shopify:block:select', (event) => {
  if (!event.target.closest('[data-islander-policy-page]')) return;
  event.target.scrollIntoView({ block: 'center', behavior: 'instant' });
});
})();
